/*
savePreset.tsx
Copyright © 2026 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/

import { useNavigate } from 'react-router'
import XrxInput from '../../components/xrx-input'
import { lazy, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRedactionContext } from '../../providers/redactionProvider'
import {
  getPresets,
  postPreset,
  updatePreset
} from '../../modules/httpClients/presetClient'
import { useToastContext } from '../../providers/useToastContext'
import { ToastType } from '../../types/toastTypes'
import { RedactProfile } from '../../modules/redactProfile'
import { useBaseContext } from '../../providers/baseProvider'
import { errorTypes } from '../../constants/errorTypes'
import { isAxiosError } from '../../modules/errors'
import OverwriteModal from '../../components/Modals/overwriteModal'
const Card = lazy(()=> import('../../components/Card')) 

export default function SavePreset () {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toastContext = useToastContext()
  const {
    draftRedactProfile,
    setDraftRedactProfile,
    setSelectedRedactProfile,
    setRedactProfiles,
    redactProfiles
  } = useRedactionContext()
  const { deviceId, setError } = useBaseContext()
  const [isValid, setIsValid] = useState<boolean | undefined>()
  const [validationMessages, setValidationMessages] = useState<string[]>([])
  const [isOverwriteModalOpen, setIsOverwriteModalOpen] = useState(false)
  const [presetToOverwrite, setPresetToOverwrite] = useState<
    RedactProfile | undefined
  >(undefined)
  const [lastPromptedName, setLastPromptedName] = useState('')
  const presetNameRequiredError = t('ERROR.ENTER_PRESET_NAME')
  const presetNameExistsError = t('ERROR.NAME_ALREADY_EXISTS')
  const getDefaultName = (currentName: string | undefined) => {
    //if not empty, add (1) to the end, if already has (1) or any number, increment the number
    if (!currentName || currentName.trim() === '') {
      return ''
    }
    const regex = /(.*?)(\s\((\d+)\))?$/
    const match = currentName.match(regex)
    if (match) {
      const baseName = match[1]
      const number = match[3] ? parseInt(match[3]) + 1 : 1
      return `${baseName} (${number})`
    }
  }
  const [presetName, setPresetName] = useState<string>(
    getDefaultName(draftRedactProfile?.name) ?? ''
  )
  const findPresetByName = (name: string) => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      return undefined
    }
    return redactProfiles.find(profile => profile.name.trim() === trimmedName)
  }

  const getNextAvailablePresetName = (name: string) => {
    let nextName = getDefaultName(name) ?? ''
    while (nextName && findPresetByName(nextName)) {
      nextName = getDefaultName(nextName) ?? ''
    }
    return nextName
  }

  const closeOverwriteModal = () => {
    setIsOverwriteModalOpen(false)
    setPresetToOverwrite(undefined)
  }

  const handlePresetPersistenceError = (err: unknown) => {
    if (isAxiosError(err)) {
      //Preset API call failed
      setError(errorTypes.apiError)
      return
    }
    toastContext.addToast(
      t('ERROR.ERROR_HAS_OCCURRED'),
      ToastType.Danger,
      false,
      true
    )
    navigate('/redactionItems')
  }

  const finalizePresetPersistence = (persistedPreset: RedactProfile) => {
    setSelectedRedactProfile(persistedPreset)
    setDraftRedactProfile(persistedPreset)
    return getPresets(
      persistedPreset.deviceGuid ?? '',
      persistedPreset.country ?? ''
    ).then((r: RedactProfile[]) => {
      setRedactProfiles(r)
      toastContext.addToast(t('PRESET_SAVED'), ToastType.Success, false, true)
      navigate('/redactionItems')
    })
  }

  const persistPreset = (
    presetToSave: RedactProfile,
    mutatePreset: (preset: RedactProfile) => Promise<number>,
    toPersistedPreset: (id: number) => RedactProfile
  ) => {
    mutatePreset(presetToSave)
      .then(id => {
        return finalizePresetPersistence(toPersistedPreset(id))
      })
      .catch(handlePresetPersistenceError)
  }

  const openOverwritePromptIfNeeded = (
    name: string,
    source: 'focus' | 'submit'
  ) => {
    const trimmedName = name.trim()
    const existingPreset = findPresetByName(trimmedName)
    if (!existingPreset || existingPreset.isDefaultProfile) {
      return false
    }

    if (source === 'focus' && lastPromptedName === trimmedName) {
      return true
    }

    setPresetToOverwrite(existingPreset)
    setIsOverwriteModalOpen(true)
    setLastPromptedName(trimmedName)
    return true
  }

  const handlePresetNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setPresetName(value)
    if (lastPromptedName && value.trim() !== lastPromptedName) {
      setLastPromptedName('')
    }

    if (isValid === false) {
      setIsValid(undefined)
      setValidationMessages([])
    }
  }

  const handlePresetNameFocus = () => {
    openOverwritePromptIfNeeded(presetName, 'focus')
  }

  const handleOverwrite = () => {
    closeOverwriteModal()

    if (!draftRedactProfile || !presetToOverwrite) {
      toastContext.addToast(
        t('ERROR.ERROR_HAS_OCCURRED'),
        ToastType.Danger,
        false,
        true
      )
      return
    }

    const presetToSave = {
      ...draftRedactProfile,
      id: presetToOverwrite.id,
      name: presetToOverwrite.name,
      isCustom: presetToOverwrite.isCustom,
      isDefaultProfile: presetToOverwrite.isDefaultProfile,
      deviceGuid: presetToOverwrite.deviceGuid || deviceId || ''
    }
    persistPreset(
      presetToSave,
      updatePreset,
      () => presetToSave
    )
  }

  const handleSaveAsNew = () => {
    const nextAvailableName = getNextAvailablePresetName(presetName)
    closeOverwriteModal()
    setIsValid(undefined)
    setValidationMessages([])
    setLastPromptedName('')
    if (nextAvailableName) {
      setPresetName(nextAvailableName)
    }
  }

  const handleOk = () => {
    // validate required field
    const trimmedPresetName = presetName.trim()
    if (!trimmedPresetName) {
      setIsValid(false)
      setValidationMessages([presetNameRequiredError])
      return
    }

    const existingPreset = findPresetByName(trimmedPresetName)
    if (existingPreset?.isDefaultProfile) {
      setIsValid(false)
      setValidationMessages([presetNameExistsError])
      return
    }

    if (openOverwritePromptIfNeeded(trimmedPresetName, 'submit')) {
      return
    }

    // Proceed with saving the preset
    if (draftRedactProfile) {
      const presetToSave = {
        ...draftRedactProfile,
        id: 0,
        name: trimmedPresetName,
        isCustom: true,
        deviceGuid: deviceId ?? ''
      }
      persistPreset(
        presetToSave,
        postPreset,
        id => ({ ...presetToSave, id })
      )
    }
  }

  const handleCancel = () => navigate(-1)

  return (
    <>
      <Card title={t('SAVE_PRESET')} onOk={handleOk} onCancel={handleCancel} XPadding='none'>
        <div className='row justify-content-center mx-3_75'>
          <div className='col-12 px-0'>
            <XrxInput
              id='save-preset'
              inputType='clearable'
              placeholder=''
              label={t('PRESET_NAME')}
              value={presetName}
              onChange={handlePresetNameChange}
              onFocus={handlePresetNameFocus}
              ariaLabel={t('PRESET_NAME')}
              onClear={() => {
                setPresetName('')
                setLastPromptedName('')
                setIsValid(undefined)
                setValidationMessages([])
              }}
              autoFocus
              valid={isValid}
              validationMessages={validationMessages}
            />
          </div>
        </div>
      </Card>
      <OverwriteModal
        isOpen={isOverwriteModalOpen}
        onClose={closeOverwriteModal}
        disableOverwrite={presetToOverwrite?.isDefaultProfile}
        onOverwrite={handleOverwrite}
        onSaveAsNew={handleSaveAsNew}
        presetName={presetToOverwrite ? presetToOverwrite.name : ''}
      />
    </>
  )
}
